import React from 'react';
import {View} from 'react-native';
import {useSelector} from 'react-redux';
import {get, isEmpty} from 'lodash';

import FormattedNumber from '/component/base/FormattedNumber';
import Text from '/component/base/Text';
import {ExpiringWellnessCreditsVolume} from '/component/partial/reportCard/ExpiringWellnessCreditsReportContent/ExpiringWellnessCreditsVolume/ExpiringWellnessCreditsVolume';
import {CoachInfoCard, UserProfileModal} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {formatTimeByOffset, getDateUtilDefaultInstance} from '/util/date.util';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {withHOC} from '/util/hoc.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './Volume.style';
interface WellnessCreditsHistoryItem {
  amount: number;
  earnedOn: string;
  expiresOn: string;
}
interface ReportRecordData {
  premierMember: boolean;
  lastOrderedOn: string;
  pqv: number;
  coach: {
    firstName?: string;
    lastName?: string;
  };
  wellnessCredits: {
    balance: number;
    currencyCode: string;
    history: WellnessCreditsHistoryItem[];
  };
}
interface VolumeProps {
  tt: (key: string, fallback?: string) => string;
  reportRecordData: ReportRecordData;
}

export const Volume: React.FC<VolumeProps> = ({tt, reportRecordData}) => {
  const {
    premierMember,
    lastOrderedOn,
    pqv,
    coach: {firstName: coachFirstName = '', lastName: coachLastName = ''} = {},
    wellnessCredits,
  } = reportRecordData ?? {};

  const featureFlag = useSelector(variablesFeatureFlag);
  const updatePremierPlus = shouldReleaseFeature(
    get(featureFlag, 'ff_Premier_Plus', ''),
  );

  const convertedToLastOrderedOnTime = formatTimeByOffset(lastOrderedOn);

  return (
    <View style={styles.footer}>
      {wellnessCredits?.currencyCode && (
        <ExpiringWellnessCreditsVolume
          {...testProperties(null, true)}
          reportRecordData={reportRecordData}
        />
      )}
      <View style={styles.footerRow}>
        <Text variant="button">
          {tt(
            'footer.premier.label',
            updatePremierPlus ? 'Premier+ Member' : 'Premier Member',
          )}
        </Text>
        <Text emphasis="low" testID={UserProfileModal.premierMember}>
          {premierMember ? 'Yes' : 'No'}
        </Text>
      </View>
      <View style={styles.footerRow}>
        <Text variant="button">{tt('footer.coach.label', 'Coach')}</Text>
        <Text emphasis="low" testID={UserProfileModal.businessCoach}>
          {coachFirstName} {coachLastName}
        </Text>
      </View>
      <View style={styles.footerRow}>
        <Text variant="button">{tt('footer.pqv.label', 'PQV')}</Text>
        <FormattedNumber
          testID={CoachInfoCard.pqv}
          value={pqv}
          textEmphasis="low"
          boldText={false}
          truncate
          preserveNegative
        />
      </View>
      <View style={styles.footerRow}>
        <Text variant="button">
          {tt('footer.lastOrder.label', 'Last Order Date')}
        </Text>
        <Text emphasis="low" testID={UserProfileModal.lastOrderedOn}>
          {isEmpty(convertedToLastOrderedOnTime)
            ? tt('lastOrderDate.na', 'N/A')
            : getDateUtilDefaultInstance().formatDate(
                convertedToLastOrderedOnTime,
                'MMM d, yyyy',
              )}
        </Text>
      </View>
    </View>
  );
};

export default withHOC(withTranslation('reportCard.client'))(Volume);
